/**
 * N LINES inspector (W2-06; DESIGN.md §4.11 "6 LINES (4 line, 2 image)", §5.5): several selected lines edited
 * together, in export order:
 *  - name pattern (`{i}` = position 0..n-1, `{n}` = 1..n, `{name}` = the current name) with a live preview;
 *  - type, parent (lines that would loop are greyed), also turn with parent, draw order (a number or an expression
 *    such as `i` or `i+10`), hide notes under the line, group, tag colour and image, all showing "Mixed" when the
 *    lines differ;
 *  - "Set at playhead" per property: a keyframe at the playhead on every selected line (Core.keys.insertKeysAt,
 *    shape-preserving), optionally with a value, in one undoable step.
 */

import { useCallback, useMemo, useState } from 'react';
import type { AppServices, InspectorSectionDef, InspectorSectionProps } from '../../../app/registry/types';
import type { LineTypeChoice } from '../../../core/commands/types';
import type { AssetRef, ChartDoc, DocIndexes, Line, LineId, LineProp } from '../../../core/model/types';
import { useLocale, useT } from '../../../i18n/useT';
import type { MessageKey } from '../../../i18n/types';
import type { DocStore } from '../../../state/types';
import {
  Badge,
  Button,
  Checkbox,
  ColorField,
  Field,
  NumberField,
  Popover,
  Section,
  Select,
  TextField,
} from '../../../ui';
import {
  exprLinesZOrder,
  keyLinesAt,
  patchLines,
  patchLinesEditor,
  propsOfGroup,
  renameLines,
  setTextureOfLines,
  type PlayheadGroup,
} from '../commands';
import { useDocValue, useEditorValue } from '../hooks';
import { fmtList, LINE_TYPE_ICON, LINE_TYPE_LABEL } from '../labels';
import { changeLineType, changeParent } from '../lineFlows';
import { ParentPicker } from '../ParentPicker';
import { m } from '../inspector.messages';
import {
  applyNamePattern,
  checkOf,
  fieldOf,
  hexToRgb,
  imageAssets,
  lineTypeOf,
  linesInExportOrder,
  lineTypeOptions,
  parentOptions,
  colorFieldOf,
  rgbToHex,
  typeCounts,
  type ParentOption,
} from '../model';
import { Readout, Row, Title, useSectionBase, useStore, WithInfo, type SectionBase } from '../parts';
import s from '../Inspector.module.css';
import { newSeed, playheadBeat, useEditKit, type EditKit } from '../tx';
import { FIELD, useFieldProblems } from '../useFieldProblems';

interface LinesDerived {
  readonly lines: readonly Line[];
  readonly types: readonly LineTypeChoice[];
  readonly parents: readonly ParentOption[];
  readonly groups: ReadonlyArray<{ readonly id: number; readonly name: string }>;
  readonly images: readonly AssetRef[];
}

function sameLinesDerived(a: LinesDerived, b: LinesDerived): boolean {
  return (
    a.lines.length === b.lines.length &&
    a.lines.every((l, i) => l === b.lines[i]) &&
    a.types.every((x, i) => x === b.types[i]) &&
    JSON.stringify(a.parents) === JSON.stringify(b.parents) &&
    JSON.stringify(a.groups) === JSON.stringify(b.groups) &&
    a.images.length === b.images.length &&
    a.images.every((x, i) => x === b.images[i])
  );
}

export function MultiLineSection(props: InspectorSectionProps) {
  const store = useStore(props.services);
  if (store === null || props.selection.lines.size < 2) return null;
  return (
    <MultiLineBody
      key={[...props.selection.lines].sort((a, b) => a - b).join(',')}
      services={props.services}
      store={store}
      selected={props.selection.lines}
    />
  );
}

function MultiLineBody(props: {
  readonly services: AppServices;
  readonly store: DocStore;
  readonly selected: ReadonlySet<LineId>;
}) {
  const { services, store } = props;
  const t = useT();
  const locale = useLocale();
  const base = useSectionBase(services, store);
  const key = [...props.selected].sort((a, b) => a - b).join(',');
  const selector = useCallback(
    (doc: ChartDoc, idx: DocIndexes): LinesDerived => {
      const ids = linesInExportOrder(idx, key === '' ? [] : (key.split(',').map(Number) as LineId[])).filter((id) =>
        doc.lines.has(id),
      );
      const lines = ids.map((id) => doc.lines.get(id) as Line);
      return {
        lines,
        types: lines.map((l) => lineTypeOf(doc, idx, l)),
        parents: parentOptions(doc, idx, ids),
        groups: [...doc.groups.values()].sort((a, b) => a.order - b.order).map((g) => ({ id: g.id, name: g.name })),
        images: imageAssets(doc),
      };
    },
    [key],
  );
  const d = useDocValue(store, selector, ['lines', 'lineOrder', 'tracks', 'groups', 'assets'], sameLinesDerived);
  const ids = useMemo(() => d.lines.map((l) => l.id), [d.lines]);
  const edit = useEditKit(services, store, `lines:${ids.join(',')}`);
  const badges = useFieldProblems(services, { lines: ids });
  const [seed, setSeed] = useState(newSeed);
  const [pattern, setPattern] = useState<string | null>(null);
  const n = d.lines.length;
  if (n === 0) return null;

  const names = fieldOf(d.lines.map((l) => l.name));
  const nameText = names?.kind === 'value' ? names.value : '';
  const type = fieldOf(d.types);
  const parent = fieldOf(d.lines.map((l) => l.parent));
  const rotate = checkOf(d.lines.map((l) => l.rotateWithParent));
  const zOrder = fieldOf(d.lines.map((l) => l.zOrder));
  const cover = checkOf(d.lines.map((l) => l.cover));
  const group = fieldOf(d.lines.map((l) => l.group));
  const tags = d.lines.map((l) => (l.editor.tag === null ? null : hexToRgb(l.editor.tag)));
  const tag = colorFieldOf(tags);
  const texture = fieldOf(d.lines.map((l) => l.texture));
  const counts = typeCounts(d.types);
  const typeOptions = lineTypeOptions(base.level, null);
  const preview = pattern === null ? null : d.lines.slice(0, 3).map((l, i) => applyNamePattern(pattern, i, l.name));

  const patchAll = (field: string, label: MessageKey, patch: Parameters<typeof patchLines>[1]['patch']): void => {
    edit.commit(field, { key: label }, (tx) => {
      patchLines(tx, { lines: ids, patch });
    });
  };

  return (
    <Section
      title={
        <Title
          main={t(m.linesTitle, { count: n })}
          detail={fmtList(
            locale,
            counts.map(([tp, c]) => t(m.typeCount, { count: c, type: t(LINE_TYPE_LABEL[tp]) })),
          )}
        />
      }
      actions={base.level === 'guided' ? <Badge kind="full" tooltip={{ key: m.fullSectionTip }} /> : undefined}
    >
      <div className={s.fields}>
        {/* ---------------- Name pattern */}
        <Row edit={edit} field={FIELD.lineName}>
          <TextField
            label={t(m.namePattern)}
            value={names ?? ''}
            helpId={FIELD.lineName}
            onChange={(text) => {
              // Escape restores the field text: nothing will be applied, so the preview goes away.
              setPattern(text === nameText ? null : text);
            }}
            onCommit={(p) => {
              setPattern(null);
              // Mixed names and an emptied field: nothing was typed, never blank every name.
              if (names?.kind !== 'value' && p === '') return;
              edit.commit(FIELD.lineName, { key: m.txLinesName }, (tx) => renameLines(tx, { lines: ids, pattern: p }));
            }}
          />
          <Readout>{t(m.namePatternHelp, { last: n - 1, count: n })}</Readout>
          {preview !== null && (
            <Readout testId="name-preview">{t(m.namePatternPreview, { names: fmtList(locale, preview) })}</Readout>
          )}
        </Row>

        {/* ---------------- Type */}
        <Row edit={edit} field={FIELD.lineType}>
          <Field label={t(m.lineType)} helpId={FIELD.lineType} info={{ text: { key: m.lineTypeInfo } }}>
            <Select<LineTypeChoice>
              ariaLabel={t(m.lineType)}
              value={type === null || type.kind === 'mixed' ? 'mixed' : type.value}
              options={typeOptions.map((tp) => ({
                value: tp,
                icon: LINE_TYPE_ICON[tp],
                label: t(LINE_TYPE_LABEL[tp]),
              }))}
              onChange={(target) => {
                void changeLineType(services, store, ids, target);
              }}
            />
          </Field>
        </Row>

        {/* ---------------- Parent */}
        <Row edit={edit} field={FIELD.lineParent}>
          <Field
            label={t(m.lineParent)}
            helpId={FIELD.lineParent}
            help={{ key: m.parentHelp }}
            badges={badges(FIELD.lineParent)}
            info={{ text: { key: m.parentInfo }, rpeTerm: 'father', communityTerm: '父线' }}
          >
            <ParentPicker
              label={t(m.lineParent)}
              value={
                parent === null || parent.kind === 'mixed'
                  ? 'mixed'
                  : parent.value === null
                    ? 'none'
                    : String(parent.value)
              }
              options={d.parents}
              onChange={(next) => {
                void changeParent(services, store, ids, next);
              }}
            />
          </Field>
        </Row>
        <Row edit={edit} field={FIELD.lineRotateWithParent}>
          <WithInfo
            info={{ text: m.rotateWithParentSentence, rpe: 'rotateWithFather', illustration: 'rotateWithParent' }}
          >
            <Checkbox
              label={t(m.rotateWithParent)}
              checked={rotate}
              helpId={FIELD.lineRotateWithParent}
              onChange={(on) => {
                patchAll(FIELD.lineRotateWithParent, m.txRotateWithParent, { rotateWithParent: on });
              }}
            />
          </WithInfo>
        </Row>

        {/* ---------------- Draw order */}
        <Row edit={edit} field={FIELD.lineZOrder}>
          <NumberField
            label={t(m.lineZOrder)}
            value={zOrder ?? { kind: 'mixed' }}
            step={1}
            precision={0}
            vars="field"
            randomSeed={seed}
            help={{ key: m.zOrderExprHelp }}
            helpId={FIELD.lineZOrder}
            badges={badges(FIELD.lineZOrder)}
            onCommit={(v) => {
              if (v.kind === 'number') {
                patchAll(FIELD.lineZOrder, m.txZOrder, { zOrder: Math.round(v.value) });
                return;
              }
              const prog = v.program;
              edit.commit(FIELD.lineZOrder, { key: m.txZOrder }, (tx) =>
                exprLinesZOrder(tx, { lines: ids, prog, seed }),
              );
              if (prog.usesRand) setSeed(newSeed());
            }}
            onScrub={(v, final) => {
              edit.scrub(
                FIELD.lineZOrder,
                { key: m.txZOrder },
                (tx) => {
                  patchLines(tx, { lines: ids, patch: { zOrder: Math.round(v) } });
                },
                final,
              );
            }}
          />
          <Readout>{t(m.lineZOrderHelp)}</Readout>
        </Row>

        {/* ---------------- Cover, group, tag */}
        <Row edit={edit} field={FIELD.lineCover}>
          <WithInfo info={{ text: m.lineCoverInfo, rpe: 'isCover', community: '遮罩', illustration: 'cover' }}>
            <Checkbox
              label={t(m.lineCover)}
              checked={cover}
              helpId={FIELD.lineCover}
              onChange={(on) => {
                patchAll(FIELD.lineCover, m.txCover, { cover: on });
              }}
            />
          </WithInfo>
        </Row>
        <Row edit={edit} field={FIELD.lineGroup}>
          <Field label={t(m.lineGroup)} helpId={FIELD.lineGroup}>
            <Select<string>
              ariaLabel={t(m.lineGroup)}
              value={
                group === null || group.kind === 'mixed' ? 'mixed' : group.value === null ? 'none' : String(group.value)
              }
              options={[
                { value: 'none', label: t(m.groupDefault) },
                ...d.groups.map((g) => ({ value: String(g.id), label: g.name })),
              ]}
              onChange={(v) => {
                patchAll(FIELD.lineGroup, m.txGroup, {
                  group: v === 'none' ? null : (Number(v) as NonNullable<Line['group']>),
                });
              }}
            />
          </Field>
        </Row>
        <Row edit={edit} field={FIELD.lineTag}>
          <ColorField
            label={t(m.lineTag)}
            value={tag}
            allowNone
            helpId={FIELD.lineTag}
            onCommit={(c) => {
              const value = c === null ? null : rgbToHex(c);
              edit.commit(FIELD.lineTag, { key: m.txTag }, (tx) => {
                patchLinesEditor(tx, { lines: ids, patch: { tag: value } });
              });
            }}
          />
        </Row>

        {/* ---------------- Image */}
        <Row edit={edit} field={FIELD.lineTexture}>
          <Field label={t(m.lineImage)} helpId={FIELD.lineTexture} badges={badges(FIELD.lineTexture)}>
            <Select<string>
              ariaLabel={t(m.lineImage)}
              value={texture === null || texture.kind === 'mixed' ? 'mixed' : (texture.value ?? 'none')}
              options={[{ value: 'none', label: t(m.none) }, ...d.images.map((a) => ({ value: a.id, label: a.path }))]}
              onChange={(v) => {
                const asset = v === 'none' ? null : (d.images.find((a) => a.id === v)?.id ?? null);
                edit.once({ key: m.txTexture }, (tx) => {
                  setTextureOfLines(tx, { lines: ids, asset });
                });
              }}
            />
          </Field>
          <Readout>{t(m.textureAll)}</Readout>
        </Row>

        {/* ---------------- Set at playhead */}
        <SetAtPlayhead services={services} base={base} edit={edit} ids={ids} />
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Set at playhead
// ---------------------------------------------------------------------------------------------------------------

interface GroupSpec {
  readonly group: PlayheadGroup;
  readonly label: MessageKey;
  readonly fields: ReadonlyArray<{
    readonly prop: LineProp;
    readonly label: MessageKey;
    readonly unit: string;
    readonly toRaw: (v: number) => number;
  }>;
}

const GROUPS = (t: ReturnType<typeof useT>): readonly GroupSpec[] => [
  {
    group: 'alpha',
    label: m.setOpacity,
    fields: [{ prop: 'alpha', label: m.propAlpha, unit: t(m.percent), toRaw: (v) => (v * 255) / 100 }],
  },
  {
    group: 'position',
    label: m.setPosition,
    fields: [
      { prop: 'moveX', label: m.propMoveX, unit: t(m.px), toRaw: (v) => v },
      { prop: 'moveY', label: m.propMoveY, unit: t(m.px), toRaw: (v) => v },
    ],
  },
  {
    group: 'rotate',
    label: m.setRotation,
    fields: [{ prop: 'rotate', label: m.propRotate, unit: t(m.deg), toRaw: (v) => v }],
  },
  { group: 'speed', label: m.setSpeed, fields: [{ prop: 'speed', label: m.propSpeed, unit: '', toRaw: (v) => v }] },
];

/** The "Set at playhead" buttons of N LINES, each opening a small form. */
export function SetAtPlayhead(props: {
  readonly services: AppServices;
  readonly base: SectionBase;
  readonly edit: EditKit;
  readonly ids: readonly LineId[];
}) {
  const { services, base, edit, ids } = props;
  const t = useT();
  const activeLayer = useEditorValue(services, (st) => st.activeLayer);
  const layer = base.level === 'full' ? activeLayer : 0;
  return (
    <div className={s.row} data-help-id="lines.setAtPlayhead">
      <span className={s.subheading}>{t(m.setAtPlayhead)}</span>
      <Readout>{t(m.setAtPlayheadHelp)}</Readout>
      <div className={s.actions}>
        {GROUPS(t).map((g) => (
          <SetGroupPopover
            key={g.group}
            spec={g}
            onSet={(values) => {
              const at = playheadBeat(services, base.tempo);
              edit.once({ key: m.txSetAtPlayhead, params: { count: ids.length } }, (tx) =>
                keyLinesAt(tx, { lines: ids, props: propsOfGroup(g.group), layer, t: at, values }),
              );
            }}
            count={ids.length}
          />
        ))}
      </div>
    </div>
  );
}

function SetGroupPopover(props: {
  readonly spec: GroupSpec;
  readonly count: number;
  onSet(values: Partial<Record<LineProp, number>>): void;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<Partial<Record<LineProp, number>>>({});
  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setValues({});
      }}
      label={t(props.spec.label)}
      trigger={
        <Button size="sm" icon="KeyRound">
          {t(props.spec.label)}
        </Button>
      }
    >
      <div className={s.popoverBody}>
        {props.spec.fields.map((f) => (
          <NumberField
            key={f.prop}
            label={t(f.label)}
            stacked
            value={
              values[f.prop] === undefined ? { kind: 'mixed' } : { kind: 'value', value: values[f.prop] as number }
            }
            unit={f.unit}
            step={1}
            precision={3}
            onCommit={(v) => {
              if (v.kind === 'number') setValues((x) => ({ ...x, [f.prop]: v.value }));
            }}
          />
        ))}
        <Readout>{t(m.keepCurrent)}</Readout>
        <Button
          variant="primary"
          size="sm"
          icon="Check"
          onClick={() => {
            const raw: Partial<Record<LineProp, number>> = {};
            for (const f of props.spec.fields) {
              const v = values[f.prop];
              if (v !== undefined) raw[f.prop] = f.toRaw(v);
            }
            props.onSet(raw);
            setOpen(false);
            setValues({});
          }}
        >
          {t(m.setOnLines, { count: props.count })}
        </Button>
      </div>
    </Popover>
  );
}

export const multiLineSectionDef: InspectorSectionDef = {
  id: 'multiLine',
  order: 301,
  level: 'full',
  appliesTo: (sel) => sel.lines.size >= 2,
  component: MultiLineSection,
};
